import type { ExtendedRecordMap } from "notion-types";

import {
  MINDMAP_NODE_CAP,
  MINDMAP_WALK_DEPTH_LIMIT,
  moreStubText,
} from "./mindmapConstants";

/**
 * Pure block-outline → tree builder for the auto-mindmap.
 *
 * Given a normalized Notion recordMap and the id of its root page, this walks
 * the block outline and produces a `MindmapTree`: the shape the layout and
 * emitter downstream consume. It applies the spec's block → node mapping
 * (see docs/superpowers/specs/2026-07-17-notion-auto-mindmap-design.md).
 *
 * The derivation contract:
 *  - Prose (paragraph "text" + quote) is excluded at EVERY depth — it never
 *    becomes a node. Callouts remain, as structural nodes that expand their
 *    own children.
 *  - Heading ranks are RELATIVE: the sequence-nesting `attach` below makes the
 *    highest heading rank actually PRESENT on the page a level-1 node, and a
 *    skipped rank nests exactly one level deeper (see `mapChildren`).
 *  - Every node carries `hasChildren`, so the skeleton (root + level 1) that
 *    generation emits can tell which nodes to draw an expand chip on.
 *
 * `buildMindmapTree` returns the FULL derived tree (the caps are a backstop, no
 * longer the primary size control); `buildMindmapSkeleton` slices it to the
 * root + level-1 shape generation actually emits.
 */

/**
 * What a node stands for. "database" is a Notion database (the root of a
 * database map, or a standalone database card): it is NOT a page, so the
 * renderer's page-only side peek must skip it — opening a data_source id as a
 * page always errors.
 */
export type MindmapNodeKind = "page" | "block" | "database" | "text";

/**
 * Where a native node's content came from (the agent tools' `source`): a link the
 * agent or user recorded, not a sync relationship. Stored on the element as
 * `customData.mindmap.provenance` (see `MindmapCustomData` for the naming).
 */
export interface MindmapNodeProvenance {
  kind: "url" | "file" | "notion" | "none";
  ref?: string;
  title?: string;
}

export interface MindmapTree {
  blockId: string;
  text: string;
  /**
   * "text" is a native node with no Notion behind it (agent- or user-made):
   * layout and emit treat it as a block, and a Notion refresh never removes
   * it. Carried here, not only on the element, so tree-level code can tell
   * it from a Notion block node — both have no `pageId`.
   */
  kind: MindmapNodeKind;
  /** Present only on `kind: "page"` nodes (root + child pages). */
  pageId?: string;
  /**
   * An explicit branch colour (a palette hex) chosen for this node; absent
   * means the positional palette applies. Inherited by the subtree unless a
   * descendant sets its own. See `assignBranchColors`.
   */
  color?: string;
  /** The agent's explicit palette name, retained when names share one hex. */
  colorName?: string;
  /** Native nodes only: the link this node stands for. */
  provenance?: MindmapNodeProvenance;
  /**
   * The node's Notion icon (emoji / external / file), when known — currently
   * database rows, whose official-API row object carries one. Threaded through
   * to the emitted `mindmapNode.icon` so the renderer draws it. Optional: page
   * and block nodes derived from a recordMap don't set it.
   */
  icon?: { type: string; [key: string]: string } | null;
  /** True on a synthetic "+N more" stub standing in for trimmed children. */
  truncated?: boolean;
  /**
   * True when this node's source has mappable children — i.e. expanding it
   * would yield at least one child node. Set by `buildMindmapTree` on every
   * node it derives so the skeleton (root + level 1) can flag which level-1
   * nodes get an expand chip even after their subtrees are sliced off.
   * Optional so hand-built trees and the database builder need not set it.
   */
  hasChildren?: boolean;
  children: MindmapTree[];
}

type NotionBlockValue = {
  id: string;
  type: string;
  properties?: { title?: unknown[]; checked?: unknown[] };
  content?: string[];
  format?: { toggleable?: boolean };
};

/** Prefixes prepended to a to_do node's text, per its checked state. */
const TODO_UNCHECKED = "☐ ";
const TODO_CHECKED = "☑ ";

/** Notion stores a checked to_do as `properties.checked = [["Yes"]]`. */
const isToDoChecked = (block: NotionBlockValue): boolean => {
  const checked = block.properties?.checked;
  return (
    Array.isArray(checked) && String((checked[0] as unknown[])?.[0]) === "Yes"
  );
};

/**
 * Notion block types that behave as headings, ordered by depth (h1 → h3).
 *
 * Notion's 4th heading level, `header_4`, is downgraded to `sub_sub_header` by
 * normalizeRecordMap BEFORE the tree builder runs, so it participates here at
 * rank 3 (h4 shares rank 3 — a deliberate, accepted fidelity loss). Ranks are
 * used relatively: `attach` (in `mapChildren`) nests a heading under the
 * nearest strictly-shallower rank that is actually OPEN, so the highest rank
 * present on the page becomes a level-1 node and skipped ranks nest one deeper.
 */
const HEADING_LEVEL: Record<string, number> = {
  header: 1,
  sub_header: 2,
  sub_sub_header: 3,
};

/**
 * A heading rank lookup that is safe against block types colliding with
 * `Object.prototype` keys ("constructor", "toString", …) — `in` would report
 * those as headings and read a function as their level.
 */
const headingLevel = (type: string): number | undefined =>
  Object.hasOwn(HEADING_LEVEL, type) ? HEADING_LEVEL[type] : undefined;

/** A toggle heading owns real children; it does not open a sequence scope. */
const isToggleHeading = (block: NotionBlockValue): boolean =>
  headingLevel(block.type) !== undefined && block.format?.toggleable === true;

/**
 * Block types that become structural nodes and nest their own `content`
 * children. Callouts are included (V2: they "count as structure"); paragraphs
 * and quotes are deliberately absent — prose never becomes a node.
 */
const STRUCTURAL_TYPES = new Set([
  "bulleted_list",
  "numbered_list",
  "to_do",
  "toggle",
  "callout",
]);

/**
 * Layout-only wrappers that carry no content of their own but OWN the blocks a
 * reader sees. Skipping them (the pre-fix behaviour) dropped their whole
 * subtree, so a two-column page or a synced block mapped as EMPTY. They are
 * "transparent": the walk splices their `content` ids into the CURRENT level's
 * iteration, so a column's headings/bullets read — and open heading scopes —
 * exactly as they would in a single-column page.
 */
const TRANSPARENT_TYPES = new Set([
  "column_list",
  "column",
  "transclusion_container",
  "transclusion_reference",
]);

type BlockRole =
  | {
      role: "node";
      kind: MindmapNodeKind;
      /** Whether this block contributes its own `content` ids as children. */
      expandContent: boolean;
    }
  /** Not a node: descend into `content` and splice into the current level. */
  | { role: "transparent" };

/**
 * Decide how a block participates in the map, or that it is skipped entirely.
 *
 * Returns null for skipped types: prose ("text" paragraphs and "quote" blocks,
 * excluded at every depth in V2), plus code, image, table, embed, divider, and
 * any type the outline does not mirror.
 */
const classify = (block: NotionBlockValue): BlockRole | null => {
  const type = block.type;

  if (type === "page") {
    return { role: "node", kind: "page", expandContent: false };
  }
  if (TRANSPARENT_TYPES.has(type)) {
    return { role: "transparent" };
  }
  if (headingLevel(type) !== undefined || STRUCTURAL_TYPES.has(type)) {
    return { role: "node", kind: "block", expandContent: true };
  }
  return null;
};

/** Flatten a Notion `Decoration[]` title into plain text. */
const titleText = (value: NotionBlockValue | undefined): string => {
  const title = value?.properties?.title;
  if (!Array.isArray(title)) {
    return "";
  }
  return title
    .map((segment) => (Array.isArray(segment) ? String(segment[0] ?? "") : ""))
    .join("");
};

const getBlock = (
  recordMap: ExtendedRecordMap,
  id: string,
): NotionBlockValue | undefined =>
  (recordMap.block?.[id]?.value as NotionBlockValue | undefined) ?? undefined;

/**
 * Block ids whose source is known to HAVE children that this recordMap does not
 * contain. Only the incremental outline route produces one: it fetches a bounded
 * number of levels, so a block below the fetched depth arrives with no `content`
 * even though Notion reports `has_children`. Without this the block would derive
 * `hasChildren: false` and lose its expand chip. Empty for a full recordMap,
 * where absent content really does mean "no children".
 */
export type UnresolvedBlocks = ReadonlySet<string>;

const NO_UNRESOLVED: UnresolvedBlocks = new Set<string>();

/**
 * Finalize each block node's `hasChildren` from its FINAL children. A heading
 * gains its children via sequence-`attach` after the node is constructed, so its
 * construction-time count is stale; this corrects it. Page nodes keep the
 * content-based flag set at construction (their children stay empty in a page
 * map, so a final-children count would wrongly read false for a child page that
 * does have content).
 */
const finalizeHasChildren = (
  node: MindmapTree,
  unresolved: UnresolvedBlocks,
): void => {
  if (node.kind === "block") {
    node.hasChildren = node.children.length > 0 || unresolved.has(node.blockId);
  }
  for (const child of node.children) {
    finalizeHasChildren(child, unresolved);
  }
};

export const buildMindmapTree = (
  recordMap: ExtendedRecordMap,
  rootPageId: string,
  unresolved: UnresolvedBlocks = NO_UNRESOLVED,
): MindmapTree => {
  const root = getBlock(recordMap, rootPageId);
  // Seed the cycle path with the root so a synced block pointing back at the
  // page cannot restart the walk.
  const children = mapChildren(recordMap, root?.content ?? [], {
    ancestors: new Set([rootPageId]),
    depth: 1,
  });

  const rootNode: MindmapTree = {
    blockId: rootPageId,
    text: titleText(root),
    kind: "page",
    pageId: rootPageId,
    hasChildren: children.length > 0,
    children,
  };

  finalizeHasChildren(rootNode, unresolved);
  return capTree(rootNode);
};

/**
 * The generation skeleton: the root plus its level-1 nodes only,
 * every level-1 node keeping its `hasChildren` flag but with its subtree sliced
 * off. This is what `generateMindmapFromPage` emits and what a page refresh
 * diffs against, so a fresh generation lands as root + level 1 while the full
 * tree stays obtainable via `buildMindmapTree` for expansion.
 *
 * Level-1 selection falls out of the sequence walk: headings/child-pages are the
 * roots when present, and — the V2 headingless fallback — top-level list/toggle
 * items are the roots when no heading opens a scope. So `full.children` are
 * already exactly the level-1 nodes; we only need to drop their descendants.
 */
export const buildMindmapSkeleton = (
  recordMap: ExtendedRecordMap,
  rootPageId: string,
  unresolved: UnresolvedBlocks = NO_UNRESOLVED,
): MindmapTree => {
  const full = buildMindmapTree(recordMap, rootPageId, unresolved);
  return {
    ...full,
    children: full.children.map((child) => ({ ...child, children: [] })),
  };
};

/** A synthetic leaf standing in for `dropped` children hidden by the caps. */
const makeStub = (parentId: string, dropped: number): MindmapTree => ({
  blockId: `${parentId}::more`,
  text: moreStubText(dropped),
  kind: "block",
  truncated: true,
  children: [],
});

/**
 * Trim the tree to the node budget, breadth-first, so every top branch keeps
 * visible structure and the deepest tails are cut first. A parent that loses
 * children gains one "+N more" stub. Stubs do not count toward the budget.
 *
 * Depth is NOT capped — see mindmapConstants. Expanding a node is the user
 * asking for that level, and the cap was cutting it off.
 *
 * Exported so the database tree builder can reuse the exact same
 * budget + "+N more" truncation the page builder applies.
 */
export const capTree = (root: MindmapTree): MindmapTree => {
  type Item = { source: MindmapTree; copy: MindmapTree; depth: number };

  const copyOf = (node: MindmapTree): MindmapTree => ({
    ...node,
    children: [],
  });

  const rootCopy = copyOf(root);
  let admitted = 1; // the root itself
  const queue: Item[] = [{ source: root, copy: rootCopy, depth: 0 }];

  while (queue.length > 0) {
    const { source, copy, depth } = queue.shift()!;
    let dropped = 0;

    for (const child of source.children) {
      const childDepth = depth + 1;
      if (admitted < MINDMAP_NODE_CAP) {
        const childCopy = copyOf(child);
        admitted++;
        copy.children.push(childCopy);
        queue.push({ source: child, copy: childCopy, depth: childDepth });
      } else {
        dropped++;
      }
    }

    if (dropped > 0) {
      copy.children.push(makeStub(copy.blockId, dropped));
    }
  }

  return rootCopy;
};

/**
 * Map an ordered list of child block ids into mindmap nodes.
 *
 * Three nesting rules run in the same pass:
 *  - Non-toggle headings sequence-nest by RELATIVE rank: a heading attaches to
 *    the nearest strictly-shallower rank that is actually OPEN (so the highest
 *    rank present becomes a level-1 root, and a skipped rank nests one deeper).
 *    Each heading also becomes the scope that following non-heading blocks fall
 *    under, so a page reads as an outline.
 *  - A child page (`kind: "page"`) is a structural top-level entity — Notion
 *    headings don't truly contain following blocks — so it is always a sibling
 *    at this level (a level-1 sibling at the root), never swallowed by an open
 *    heading scope.
 *  - Everything else nests structurally via its own `content` ids (bullets,
 *    toggles, toggle-headings).
 *
 * The walk is guarded (`ctx`) because a recordMap is not guaranteed acyclic:
 * a synced block whose content transitively references its own container makes
 * `content` links form a cycle, and an unguarded recursion blows the stack with
 * a RangeError — which the fire-and-forget expand call sites swallowed
 * silently. `ancestors` is the id path from the root, so a block may still
 * legitimately appear in two different branches; only re-entering a block that
 * already contains us is a cycle. `depth` is the stack guard — see
 * MINDMAP_WALK_DEPTH_LIMIT; it is deliberately far deeper than any real page.
 */
type WalkContext = {
  /** Block ids on the current path from the root — a repeat is a cycle. */
  ancestors: Set<string>;
  /** Node depth of the level being built; the root's own content is 1. */
  depth: number;
};

const mapChildren = (
  recordMap: ExtendedRecordMap,
  contentIds: string[],
  ctx: WalkContext = { ancestors: new Set(), depth: 1 },
): MindmapTree[] => {
  const roots: MindmapTree[] = [];
  if (ctx.depth > MINDMAP_WALK_DEPTH_LIMIT) {
    return roots;
  }
  // Open heading scopes keyed by level (1..3). A deeper scope shadows shallower
  // ones for attaching following content.
  let headingScope: (MindmapTree | undefined)[] = [];

  const attach = (node: MindmapTree, level: number) => {
    // A node at heading `level` attaches to the nearest strictly-shallower open
    // scope; a non-heading (level 0) attaches to the deepest open scope.
    for (let i = level === 0 ? 3 : level - 1; i >= 1; i--) {
      const parent = headingScope[i];
      if (parent) {
        parent.children.push(node);
        return;
      }
    }
    roots.push(node);
  };

  /** Descend into `id`'s content with `id` added to the cycle path. */
  const descend = (id: string, depth: number): MindmapTree[] => {
    const block = getBlock(recordMap, id);
    ctx.ancestors.add(id);
    const out = mapChildren(recordMap, block?.content ?? [], {
      ancestors: ctx.ancestors,
      depth,
    });
    ctx.ancestors.delete(id);
    return out;
  };

  // The ids still to process. A layout wrapper is not walked as a subtree; its
  // content ids are spliced in HERE (#E5 follow-up) so each spliced block runs
  // the same classify → attach → open-scope path as an inline block. Building
  // finished subtrees and re-attaching them instead lost the heading scope, so a
  // heading inside a column was adopted by the heading preceding the column.
  // The `exitWrapper` marker pops the wrapper off the cycle path once all of its
  // spliced content has been consumed, and restores the heading scopes that were
  // open when the wrapper began: a heading is a scope over what FOLLOWS it in
  // its own column, and columns are parallel, so a heading in column 1 must not
  // adopt column 2's blocks (nor anything after the whole column_list). Headings
  // opened at page level are untouched — they only ever close here if they were
  // opened inside the wrapper.
  const queue: (
    | string
    | { exitWrapper: string; scope: (MindmapTree | undefined)[] }
  )[] = [...contentIds];

  while (queue.length > 0) {
    const item = queue.shift()!;
    if (typeof item !== "string") {
      ctx.ancestors.delete(item.exitWrapper);
      headingScope = item.scope;
      continue;
    }
    const id = item;

    const block = getBlock(recordMap, id);
    if (!block || ctx.ancestors.has(id)) {
      continue;
    }

    const role = classify(block);
    if (!role) {
      continue;
    }

    // A layout wrapper contributes no node: its content is spliced into THIS
    // level, at this depth, so a column reads like plain page content. Nested
    // wrappers (column_list → column) splice again on their own turn.
    if (role.role === "transparent") {
      ctx.ancestors.add(id);
      queue.unshift(...(block.content ?? []), {
        exitWrapper: id,
        scope: [...headingScope],
      });
      continue;
    }

    const rawText = titleText(block);
    const text =
      block.type === "to_do"
        ? `${isToDoChecked(block) ? TODO_CHECKED : TODO_UNCHECKED}${rawText}`
        : rawText;
    const children = role.expandContent ? descend(id, ctx.depth + 1) : [];
    // A block node has children when its expanded content produced any nodes.
    // A child page always gets a chip (#E11): its own content lives in ITS
    // recordMap, which is not fetched here, so the parent's `block.content` —
    // absent for a subpage — is not evidence of emptiness. `fetchChildLevel`
    // handles an empty result by toasting and flipping the chip back.
    const hasChildren = role.kind === "page" ? true : children.length > 0;
    const node: MindmapTree = {
      blockId: id,
      text,
      kind: role.kind,
      ...(role.kind === "page" ? { pageId: id } : {}),
      hasChildren,
      children,
    };

    if (role.kind === "page") {
      // Top-level child pages are level-1 siblings; they neither attach to a
      // heading scope nor open one.
      roots.push(node);
      continue;
    }

    // Only real (non-toggle) headings sequence-nest and open a scope.
    const level = isToggleHeading(block) ? 0 : headingLevel(block.type) ?? 0;

    attach(node, level);

    if (level > 0) {
      // Open this heading's scope and close every deeper one.
      headingScope[level] = node;
      for (let i = level + 1; i <= 3; i++) {
        headingScope[i] = undefined;
      }
    }
  }

  return roots;
};

/* ------------------------------------------------------------------------- *
 * Incremental outline → tree
 *
 * The generation and expand paths read one level at a time from the OFFICIAL
 * Notion API (`supabase/functions/notion-outline`) instead of downloading a
 * whole page's recordMap. That response is translated into the same flat
 * recordMap shape the walk above already consumes, rather than growing a second
 * walk: the heading-scope rule, the transparent-container splice, prose
 * exclusion and the caps are hard-won and must not exist twice. Everything
 * below is vocabulary translation only.
 * ------------------------------------------------------------------------- */

/** One child block as `notion-outline` returns it (see that function). */
export interface OutlineBlock {
  id: string;
  /** The official-API block type, e.g. "heading_1", "bulleted_list_item". */
  type: string;
  text: string;
  hasChildren: boolean;
  checked?: boolean;
  toggleable?: boolean;
  /** Present only when this block's own children were resolved in the call. */
  children?: OutlineBlock[];
  /** The walk meant to descend here and could not — see the server's own note. */
  unresolved?: boolean;
}

export interface OutlineResponse {
  blockId: string;
  title?: string;
  icon?: unknown;
  children: OutlineBlock[];
}

/**
 * Whether a response really is an outline. The edge functions answer failures
 * with a JSON body too, so a shape check — not just a truthy `data` — is what
 * routes a bad answer to the recordMap fallback instead of into the walk.
 */
export const isOutlineResponse = (data: unknown): data is OutlineResponse =>
  typeof data === "object" &&
  data !== null &&
  Array.isArray((data as { children?: unknown }).children);

/**
 * Official-API block type → the recordMap type `classify` decides on.
 *
 * Types absent here keep their own name, which `classify` does not recognise
 * and therefore skips — the same outcome the recordMap route reaches for code,
 * images, tables and embeds. `paragraph`/`quote` are listed explicitly so the
 * V2 prose-exclusion rule is visibly the same rule on both routes, and
 * `child_database` maps to the recordMap's `collection_view` so a database card
 * is skipped rather than mapped as a page (opening one as a page always errors).
 */
const OUTLINE_TYPE: Record<string, string> = {
  heading_1: "header",
  heading_2: "sub_header",
  heading_3: "sub_sub_header",
  bulleted_list_item: "bulleted_list",
  numbered_list_item: "numbered_list",
  to_do: "to_do",
  toggle: "toggle",
  callout: "callout",
  child_page: "page",
  child_database: "collection_view",
  column_list: "column_list",
  column: "column",
  // Original and reference alike: listing a reference's children returns the
  // synced content, so both are the transparent container.
  synced_block: "transclusion_container",
  paragraph: "text",
  quote: "quote",
};

/** Safe against types colliding with `Object.prototype` keys, as `classify` is. */
const recordMapType = (type: string): string =>
  Object.hasOwn(OUTLINE_TYPE, type) ? OUTLINE_TYPE[type] : type;

/**
 * Whether an outline response contains a TRANSPARENT wrapper the server could
 * not resolve (its request budget ran out, or the listing failed).
 *
 * That one case cannot be rendered honestly. A wrapper contributes no node — its
 * children are spliced into the current level — so an unresolved one is
 * indistinguishable from an empty one: the branch just disappears, with no chip
 * and no error. An unresolved BLOCK node is fine by contrast; it keeps its chip
 * and costs the user one more click. So callers use this to fall back to the
 * whole-page recordMap route instead of showing a level they know is a lie.
 */
export const outlineHasUnresolvedWrapper = (
  children: readonly OutlineBlock[],
): boolean =>
  children.some(
    (child) =>
      (child.unresolved === true &&
        TRANSPARENT_TYPES.has(recordMapType(child.type))) ||
      outlineHasUnresolvedWrapper(child.children ?? []),
  );

/**
 * Rebuild the flat recordMap the walk consumes from an outline response, plus
 * the set of blocks whose children exist but were not fetched (see
 * {@link UnresolvedBlocks}).
 */
export const outlineToRecordMap = (
  rootId: string,
  rootTitle: string,
  children: readonly OutlineBlock[],
): { recordMap: ExtendedRecordMap; unresolved: Set<string> } => {
  const block: Record<string, { role: string; value: NotionBlockValue }> = {};
  const unresolved = new Set<string>();

  const add = (node: OutlineBlock): void => {
    const nested = node.children ?? [];
    block[node.id] = {
      role: "reader",
      value: {
        id: node.id,
        type: recordMapType(node.type),
        properties: {
          title: [[node.text]],
          // The walk reads a checked to_do as `[["Yes"]]`.
          ...(node.checked === undefined
            ? {}
            : { checked: [[node.checked ? "Yes" : "No"]] }),
        },
        ...(node.toggleable ? { format: { toggleable: true } } : {}),
        content: nested.map((child) => child.id),
      },
    };
    if (node.hasChildren && node.children === undefined) {
      unresolved.add(node.id);
    }
    nested.forEach(add);
  };
  children.forEach(add);

  block[rootId] = {
    role: "reader",
    value: {
      id: rootId,
      type: "page",
      properties: { title: [[rootTitle]] },
      content: children.map((child) => child.id),
    },
  };

  return { recordMap: { block } as unknown as ExtendedRecordMap, unresolved };
};

/** The FULL tree derivable from an outline response (one level per fetch). */
export const buildTreeFromOutline = (
  outline: OutlineResponse,
  rootId: string,
  rootTitle: string,
): MindmapTree => {
  const { recordMap, unresolved } = outlineToRecordMap(
    rootId,
    rootTitle,
    outline.children,
  );
  return buildMindmapTree(recordMap, rootId, unresolved);
};

/** The generation skeleton (root + level 1) from an outline response. */
export const buildSkeletonFromOutline = (
  outline: OutlineResponse,
  rootId: string,
  rootTitle: string,
): MindmapTree => {
  const { recordMap, unresolved } = outlineToRecordMap(
    rootId,
    rootTitle,
    outline.children,
  );
  return buildMindmapSkeleton(recordMap, rootId, unresolved);
};
